/**
 * Recording key unlock orchestration – the single top-level entry point for
 * asking the user to unlock a device's encrypted recordings.
 *
 * @remarks
 * Instead of every view owning its own password prompt, there is one shared
 * trigger store ({@link camera_key_prompt}) and one `RecordingKeyPrompt` rendered
 * per top-level camera view (device drawer / sidepanel main / recordings tab).
 * Any code that needs a key calls {@link requestRecordingKey} (which opens the
 * shared prompt) or {@link ensureDeviceKey} (which resolves silently, prompting
 * only when a backup exists but no usable password is preserved).
 *
 * All device ids are normalised through `real_id` so the store/HKDF-salt key is
 * always the 12-lowercase-hex MAC — identical to `recording.mac` — with no
 * channel suffix (`_N`) leaking in.
 */

import type { Device } from 'src/types/Device';
import { real_id } from 'src/helpers/interface_helpers';
import { getCameraStorageKeys } from 'src/services/cam-cloud/recordings';
import { camera_storage_keys } from '../stores/camera_storage_keys';
import { camera_key_prompt } from '../stores/camera_key_prompt';
import { camera_setup_prompt } from '../stores/camera_setup_prompt';
import { isCameraEncryptionConfigured, isCameraEncryptionSupported } from './camera-features';
import { resolveMasterKey } from './encryption/master-key-management';

/**
 * Opens the shared recording-key prompt for a device, scoped to a recording era.
 *
 * @param deviceId - The device id (raw `device.id` or a `recording.mac`); it is
 * normalised through `real_id`, so both resolve to the same MAC.
 * @param wrongKey - When `true`, the prompt surfaces a wrong/rotated-password error.
 * @param date - Optional recording ISO `date` selecting the era to unlock (newest
 * when omitted), so the correct rotation key is recovered.
 */
export const requestRecordingKey = (deviceId: string, wrongKey = false, date = ''): void => {
	camera_key_prompt.open(real_id(deviceId), wrongKey, date);
};

/** Whether a device has an encrypted backup either locally or in the cloud. */
const hasBackup = async (deviceId: string): Promise<boolean> => {
	if (camera_storage_keys.getAll(deviceId).length) return true;
	try {
		const entries = await getCameraStorageKeys(deviceId);
		return entries.some((entry) => entry?.blob && entry?.hmac);
	} catch {
		return false;
	}
};

/**
 * Ensures a device's `master_key` is available up-front for a recording's era,
 * prompting only when needed: resolves silently from the era's preserved
 * password/backup, and when that fails **but a backup exists** (local or cloud),
 * opens the shared prompt scoped to that era. A no-op when the key resolves or the
 * device has no backup at all.
 *
 * @param deviceId - The device id (raw `device.id` or `recording.mac`); normalised
 * through `real_id`.
 * @param date - Optional recording ISO `date` selecting the era (newest when omitted).
 */
export const ensureDeviceKey = async (deviceId: string, date = ''): Promise<void> => {
	const id = real_id(deviceId);
	if (await resolveMasterKey(id, date)) return;
	if (!(await hasBackup(id))) return;
	// Auto-open at most once per device per session; the player's Unlock button
	// (via requestRecordingKey) still opens the prompt on demand afterwards.
	if (camera_key_prompt.hasPrompted(id)) return;
	camera_key_prompt.open(id, false, date);
};

/**
 * Top-level entry point for a camera device page: resolves the newest era's key
 * up-front and, when it can't, surfaces the right flow.
 *
 * - Does nothing when the firmware does not support recording encryption.
 * - Opens the setup flow when the camera reports no key of its own.
 * - Resolves silently from the preserved password/backup when possible.
 * - Opens the password prompt when a backup exists but no usable key is preserved.
 * - Opens the setup flow when no backup exists yet.
 *
 * @remarks
 * The camera is the source of truth for the first check: a backup left over from
 * before a factory reset still resolves, yet the camera could encrypt nothing with
 * it, so pairing has to run again. Older backups are kept — their recordings stay
 * decryptable via their own era.
 *
 * @param device - The camera device (the full object is needed to provision a key).
 */
export const ensureDeviceEncryption = async (device: Device): Promise<void> => {
	if (!device?.id || !isCameraEncryptionSupported(device)) return;
	const id = real_id(device.id);
	// Auto-open either prompt at most once per device per session; cheap guard up front so a
	// return visit skips the async resolve below (on-demand paths still open prompts later).
	if (camera_key_prompt.hasPrompted(id) || camera_setup_prompt.hasPrompted(id)) return;
	if (!isCameraEncryptionConfigured(device)) {
		camera_setup_prompt.open(id);
		return;
	}
	if (await resolveMasterKey(id)) return;
	if (await hasBackup(id)) {
		camera_key_prompt.open(id, false);
		return;
	}
	camera_setup_prompt.open(id);
};

/**
 * Whether a device still needs encryption set up: the firmware supports it, and
 * either the camera holds no key of its own or no key backup exists yet (locally or
 * in the cloud). Drives the main-screen Setup CTA.
 *
 * @remarks
 * Mirrors {@link ensureDeviceEncryption} so the CTA and the auto-prompt never
 * disagree: a camera that reports no key needs setup however many backups are left
 * over from before it was reset.
 */
export const needsEncryptionSetup = async (device: Device): Promise<boolean> => {
	if (!device?.id || !isCameraEncryptionSupported(device)) return false;
	if (!isCameraEncryptionConfigured(device)) return true;
	return !(await hasBackup(real_id(device.id)));
};
